/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: useAccessControlList.tsx
 ??? Created: 2025-12-06
 ?? Description:
    Hook for managing Access Control list with DataGrid
----------------------------------------------------------------*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchAccessControlEntries } from '../api/accessControlApi';
import type { AccessControlEntry } from '../types';

export function useAccessControlList() {
  const { t } = useI18n();

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('accessControl.columns.name'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span className="font-medium text-foreground">{item.name}</span>
        ),
      },
      {
        key: 'listType',
        label: t('accessControl.columns.listType'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => {
          const typeColors: Record<string, string> = {
            whitelist: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            blacklist: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
          };
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase ${typeColors[item.listType] || 'bg-gray-100 text-gray-800'
                }`}
            >
              {item.listType}
            </span>
          );
        },
      },
      {
        key: 'ipAddress',
        label: t('accessControl.columns.ipAddress'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span className="text-sm text-foreground font-mono">{item.ipAddress}</span>
        ),
      },
      {
        key: 'protocol',
        label: t('accessControl.columns.protocol'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span className="text-sm text-foreground uppercase">{item.protocol}</span>
        ),
      },
      {
        key: 'direction',
        label: t('accessControl.columns.direction'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span className="text-sm text-muted-foreground uppercase">{item.direction}</span>
        ),
      },
      {
        key: 'priority',
        label: t('accessControl.columns.priority'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span className="text-sm text-foreground font-mono">{item.priority}</span>
        ),
      },
      {
        key: 'enabled',
        label: t('accessControl.columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
              }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'source',
        label: t('accessControl.columns.source'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AccessControlEntry) => {
          const sourceColors: Record<string, string> = {
            manual: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
            fail2ban: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
            nftables: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
          };
          const sourceLabels: Record<string, string> = {
            manual: t('accessControl.source.manual'),
            fail2ban: 'Fail2Ban',
            nftables: 'NFTables',
          };
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${sourceColors[item.source] || 'bg-gray-100 text-gray-800'
                }`}
            >
              {sourceLabels[item.source] || item.source}
            </span>
          );
        },
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await fetchAccessControlEntries({
        ...params,
      });

      return {
        data: result.data,
        pagination: result.pagination,
      };
    },
    []
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'priority',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'accessControl',
  });

  const entries = useMemo(() => data as AccessControlEntry[], [data]);

  return {
    entries,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}
